"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.CustomerBooking = void 0;
const jsonUtils_1 = require("benjibooks-shared/utils/jsonUtils");
const groupBy_1 = __importDefault(require("lodash/groupBy"));
const dateUtils = __importStar(require("../utils/dateUtils"));
const dateUtils_1 = require("../utils/dateUtils");
const Booking_1 = require("./Booking");
const BookingContact_1 = require("./BookingContact");
const BookingDiscount_1 = require("./BookingDiscount");
const BookingItem_1 = require("./BookingItem");
const BookingStatus_1 = require("./BookingStatus");
const InvoiceStatus_1 = require("./InvoiceStatus");
const Payment_1 = require("./Payment");
/**
 * Booking made by a customer.
 *
 * Only available for resources that are booked by a customer.
 * For example. There will be no customer booking info if the booking is just for
 * blocking a resource in the calendar.
 */
class CustomerBooking extends Booking_1.Booking {
    constructor(resourceId, config) {
        super(resourceId, config);
        /** The status of the invoice and payments. */
        this.invoiceStatus = InvoiceStatus_1.InvoiceStatus.none;
        /**
         * The payments received from the customer.
         * There can be multiple payments if a customer doesn't send the correct amount.
         *
         * Note: Payments with a negative amount are **refunds**.
         */
        this.payments = [];
        /** The contact person and organization for this booking. */
        this.contact = new BookingContact_1.BookingContact();
        /** Estimated number of people that are expected to use the resource. */
        this.people = 0;
        /** Comments made by the customer. */
        this.commentsByCustomer = "";
        /** Comments made by the staff (visible to customer). */
        this.commentsByStaff = "";
        /**
         * Addon items.
         * Each item is a line in the offer/invoice.
         */
        this.addonItems = [];
        this.status = BookingStatus_1.BookingStatus.provisional;
    }
    static fromBooking(booking, config) {
        const customerBooking = Object.assign(new CustomerBooking(booking.getResourceId(), config), booking);
        customerBooking.status = BookingStatus_1.BookingStatus.provisional;
        return customerBooking;
    }
    /**
     * Creates a new `CustomerBooking` based on the `prevCustomerBooking` with updated fields
     * from the partial `newCustomerBooking`.
     */
    static fromPartial(newCustomerBooking, prevCustomerBooking, config) {
        return Object.assign(new CustomerBooking(prevCustomerBooking.getResourceId(), config), prevCustomerBooking, newCustomerBooking);
    }
    /**
     * Constructs an object of this class from the specified JSON-compatible dictionary object.
     */
    static fromJSON(id, json, config) {
        const { resourceId } = json;
        if (typeof resourceId !== "string") {
            throw new Error("No 'resourceId' specified in JSON for CustomerBooking.");
        }
        const obj = new CustomerBooking(resourceId, config);
        obj.assignJSON(id, json);
        return obj;
    }
    assignJSON(id, json) {
        super.assignJSON(id, json);
        // Overwrite fields that need converting.
        if ((0, jsonUtils_1.isJSONObject)(json.resourceItem)) {
            this.resourceItem = BookingItem_1.BookingItem.fromJSON(json.resourceItem, this._config, this);
        }
        else {
            throw new Error("No 'resourceItem' specified in JSON for CustomerBooking.");
        }
        if (typeof json.invoiceStatus === "string") {
            this.invoiceStatus = InvoiceStatus_1.InvoiceStatus.fromName(json.invoiceStatus);
        }
        if (typeof json.invoiceDate === "string" ||
            (0, dateUtils_1.isFirebaseTimestamp)(json.invoiceDate)) {
            this.invoiceDate = dateUtils.convertToDate(json.invoiceDate);
        }
        if (typeof json.dueDate === "string" || (0, dateUtils_1.isFirebaseTimestamp)(json.dueDate)) {
            this.dueDate = dateUtils.convertToDate(json.dueDate);
        }
        if ((0, jsonUtils_1.isJSONObject)(json.contact)) {
            this.contact = BookingContact_1.BookingContact.fromJSON(json.contact);
        }
        if ((0, jsonUtils_1.isJSONObjectArray)(json.payments)) {
            this.payments = json.payments.map((payment) => Payment_1.Payment.fromJSON(payment));
        }
        if ((0, jsonUtils_1.isJSONObject)(json.discount)) {
            this.discount = BookingDiscount_1.BookingDiscount.fromJSON(json.discount);
        }
        if ((0, jsonUtils_1.isJSONObjectArray)(json.addonItems)) {
            this.addonItems = json.addonItems.map((addonitem) => {
                return BookingItem_1.BookingItem.fromJSON(addonitem, this._config, this);
            });
        }
    }
    /**
     * Converts this object to JSON-compatible dictionary object.
     * All fields from `this` will be in the returned object except the `undefined` values.
     */
    toJSON() {
        const json = super.toJSON();
        // Overwrite fields that need converting.
        json.resourceItem = this.resourceItem.toJSON();
        json.invoiceStatus = this.invoiceStatus.name;
        json.contact = this.contact.toJSON();
        if (this.payments.length > 0) {
            json.payments = this.payments.map((payment) => payment.toJSON());
        }
        if (this.discount) {
            json.discount = this.discount.toJSON();
        }
        if (this.addonItems.length > 0) {
            json.addonItems = this.addonItems.map((addon) => addon.toJSON());
        }
        return json;
    }
    /**
     * Updates the booking to match a changed resource.
     */
    updateForResourceChange() {
        var _a;
        super.updateForResourceChange();
        if (this.resourceItem) {
            // Already had a resource item. Update with the new config.
            // There might not be a matching resource config for older bookings.
            if (this.configResource) {
                // Change resource item type/title to match the new resource config.
                this.resourceItem.type = this.configResource.resourceItem.type;
                this.resourceItem.title = this.configResource.resourceItem.title;
            }
            console.log(this.resourceItem);
            this.resourceItem.updateConfigItem(this);
        }
        else {
            // No resource item yet. Create a new item.
            this.resourceItem = new BookingItem_1.BookingItem(this._config, (_a = this.configResource) === null || _a === void 0 ? void 0 : _a.resourceItem);
            this.resourceItem.updatePriceAndUnit(this.isFullDay());
        }
        // Also update all addon items because they might not have a matching config item any more.
        if (this.addonItems) {
            this.addonItems.forEach((addonItem) => addonItem.updateConfigItem(this));
        }
    }
    /**
     * Updates the customer booking:
     *
     * 1. Updates for the current duration.
     * 2. Updates the resource and addon items.
     * 3. Sorts the payments.
     * 4. Updates the invoice state.
     *
     * Note: Addons must be sorted with a separate call to `sortAddons()` because the user
     * might want to keep the current order.
     */
    update() {
        // Set quantity to current duration.
        this.resourceItem.quantity = this.durationInHours();
        const fullDay = this.isFullDay();
        // Update resource and addons.
        this.resourceItem.updatePriceAndUnit(fullDay);
        this.addonItems.forEach((addon) => {
            addon.updatePriceAndUnit(fullDay);
        });
        // Sorts the payments in ascending date order.
        this.payments.sort((a, b) => a.date.getTime() - b.date.getTime());
        // Update invoice status.
        this.updateInvoiceStatus();
    }
    /** Returns the organization if it exists and otherwise the contact person. */
    getContactOrgOrName() {
        if (this.contact.org && this.contact.org.trim() !== "") {
            return this.contact.org;
        }
        else {
            return this.contact.name;
        }
    }
    /** Returns true if the booking duration is a full day (5 hours or more). */
    isFullDay() {
        return this.durationInHours() >= 5;
    }
    /** Returns the status that is currently relevant for the customer. */
    getRelevantStatus() {
        if (this.invoiceStatus !== InvoiceStatus_1.InvoiceStatus.none) {
            return this.invoiceStatus;
        }
        else {
            return this.status;
        }
    }
    /** Returns true if the invoice is void. */
    hasVoidedInvoice() {
        return this.invoiceStatus === InvoiceStatus_1.InvoiceStatus.void;
    }
    /** Updates the invoice status depending on dates, totals and payments. */
    updateInvoiceStatus() {
        if (this.hasVoidedInvoice()) {
            // Invoice status "void" is set by the user. We must keep this status.
            return;
        }
        const totals = this.calcTotals();
        if (totals.totalBalanceDue === 0) {
            // Zero amount does not need an invoice date.
            this.invoiceStatus = InvoiceStatus_1.InvoiceStatus.zero;
        }
        else if (this.invoiceDate) {
            if (this.payments.length > 0) {
                // Not zero amount and payments available.
                // Test if fully or partially paid.
                if (totals.outstandingBalance === 0) {
                    this.invoiceStatus = InvoiceStatus_1.InvoiceStatus.paid;
                }
                else {
                    this.invoiceStatus = InvoiceStatus_1.InvoiceStatus.partial;
                }
            }
            else {
                // Not zero amount, no payments, invoice sent.
                this.invoiceStatus = InvoiceStatus_1.InvoiceStatus.open;
            }
        }
        else {
            // No invoice date.
            this.invoiceStatus = InvoiceStatus_1.InvoiceStatus.none;
        }
    }
    /**
     * Applies the provided discount code to all items.
     */
    useDiscountCode(discountCode) {
        this.resourceItem.useDiscountCode(discountCode);
        this.addonItems.forEach((addon) => {
            addon.useDiscountCode(discountCode);
        });
    }
    /**
     * Returns the addons grouped by type property.
     *
     * The sort order of the groups is according to the order in the addons array.
     */
    getAddonsGroupedByType() {
        return (0, groupBy_1.default)(this.addonItems, (addonItem) => addonItem.type);
    }
    /**
     * Returns the addons of one specific type.
     */
    getAddonsOfType(type) {
        return this.addonItems.filter((addonItem) => addonItem.type === type);
    }
    /**
     * Sorts the addons according to the order of the config.
     *
     * Note: This only works if the `configResource` is available.
     */
    sortAddons() {
        if (this.configResource) {
            const addons = this.configResource.addonItems;
            this.addonItems.sort((a, b) => {
                const aIndex = addons.findIndex((configAddon) => configAddon.type === a.type && configAddon.title === a.title);
                const bIndex = addons.findIndex((configAddon) => configAddon.type === b.type && configAddon.title === b.title);
                return aIndex - bIndex;
            });
        }
    }
    /** Returns true if this booking has a discount either in the resource or in addons. */
    hasResourceOrAddonsDiscount() {
        return (this.resourceItem.discount !== undefined ||
            this.addonItems.some((addon) => addon.discount !== undefined));
    }
    /** Goes through all items and discounts and ensures that no titles are empty. */
    // TODO: More elegant validation for this.
    ensureNonEmptyItemAndDiscountTitles() {
        var _a, _b;
        if (((_a = this.discount) === null || _a === void 0 ? void 0 : _a.title.trim()) === "") {
            this.discount.title = "(Rabatt)";
        }
        if (((_b = this.resourceItem.discount) === null || _b === void 0 ? void 0 : _b.title.trim()) === "") {
            this.resourceItem.discount.title = "(Rabatt)";
        }
        this.addonItems.forEach((item) => {
            var _a;
            if (item.title.trim() === "") {
                item.title = "(Artikel)";
            }
            if (((_a = item.discount) === null || _a === void 0 ? void 0 : _a.title.trim()) === "") {
                item.discount.title = "(Rabatt)";
            }
        });
    }
    /**
     * Calculates and returns total prices.
     *
     * @param paidByDate If provided, only sums up payments paid before that date.
     */
    calcTotals({ paidByDate } = {}) {
        let totalPrice = 0;
        let taxes = [];
        const [resourcePrice, resourceTax] = this.calcResourcePriceAndTax();
        totalPrice += resourcePrice;
        if (resourceTax) {
            taxes.push(resourceTax);
        }
        const [addonsPrice, addonsTaxes] = this.calcAddonsPriceAndTaxes();
        totalPrice += addonsPrice;
        taxes.push(...addonsTaxes);
        const discountOnTotal = this.calcDiscount(totalPrice);
        const totalPriceAfterDiscount = totalPrice - discountOnTotal;
        const voidedAmount = this.hasVoidedInvoice() ? totalPriceAfterDiscount : 0;
        const totalBalanceDue = totalPriceAfterDiscount - voidedAmount;
        // The percentage of the total price that is actually due (after discount and voiding).
        const totalBalanceDueInPercent = totalPrice === 0 ? 1 : totalBalanceDue / totalPrice;
        // Sum up the payments. If a date is provided only up to that date.
        const totalPayments = this.calcPayments(paidByDate);
        // Balance after payments and voiding.
        const outstandingBalance = totalBalanceDue - totalPayments;
        // Group and sum taxes by tax rate.
        taxes = taxes.reduce((groupedTaxes, currentTax) => {
            const existingTax = groupedTaxes.find((t) => t.taxRate === currentTax.taxRate);
            if (existingTax) {
                existingTax.taxAmount += currentTax.taxAmount;
            }
            else {
                groupedTaxes.push(currentTax);
            }
            return groupedTaxes;
        }, []);
        // Apply discount and voiding to the taxes.
        taxes.forEach((tax) => {
            tax.taxAmount *= totalBalanceDueInPercent;
        });
        return {
            totalPrice,
            discountOnTotal,
            totalPriceAfterDiscount,
            voidedAmount,
            totalBalanceDue,
            totalBalanceDueInPercent,
            totalPayments,
            outstandingBalance,
            taxes,
        };
    }
    calcPayments(paidByDate) {
        return this.payments.reduce((accumulator, currentPayment) => {
            if (!paidByDate || currentPayment.date <= paidByDate) {
                return accumulator + currentPayment.amount;
            }
            else {
                return accumulator;
            }
        }, 0);
    }
    calcDiscount(totalPrice) {
        if (this.discount) {
            return this.discount.calc(totalPrice);
        }
        else {
            return 0;
        }
    }
    calcResourcePriceAndTax() {
        var _a, _b;
        let resourcePrice = this.resourceItem.price;
        resourcePrice -= (_b = (_a = this.resourceItem.discount) === null || _a === void 0 ? void 0 : _a.calc(resourcePrice)) !== null && _b !== void 0 ? _b : 0;
        const resourceTax = this.calcTax(resourcePrice, this.resourceItem.taxRate);
        return [resourcePrice, resourceTax];
    }
    calcAddonsPriceAndTaxes() {
        let totalAddonPrice = 0;
        const addonTaxes = [];
        this.addonItems.forEach((addon) => {
            var _a, _b;
            let addonPrice = addon.price;
            addonPrice -= (_b = (_a = addon.discount) === null || _a === void 0 ? void 0 : _a.calc(addonPrice)) !== null && _b !== void 0 ? _b : 0;
            totalAddonPrice += addonPrice;
            const addonTax = this.calcTax(addonPrice, addon.taxRate);
            if (addonTax) {
                addonTaxes.push(addonTax);
            }
        });
        return [totalAddonPrice, addonTaxes];
    }
    calcTax(grossPrice, taxRate) {
        if (taxRate <= 0) {
            return undefined;
        }
        const taxAmount = (grossPrice / (100 + taxRate)) * taxRate;
        return {
            taxRate,
            taxAmount,
        };
    }
}
exports.CustomerBooking = CustomerBooking;
